import usePaymentOrderOptions, { mergePaymentOrders } from "../../gestion-payments/usePaymentOrderOptions";
import usePaymentFormChoices from "../../gestion-payments/usePaymentFormChoices";
import BusinessResourceNotice from "../../components/BusinessResourceNotice";
// PaymentsMobile.jsx — « Paiements sortants » (Achats) et « Paiements entrants » (Ventes)
// en version mobile. Un seul écran paramétré par `paymentType`, les deux écrans desktop
// étant des jumeaux à ~95 %. CRUD complet, allocation par commande éditable.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal, Button, Alert } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, SearchBar, EmptyState, Fab, Sheet, ActionRow, DetailRow,
  Note, Field, TextInput, TextAreaInput, SelectInput, Pill, Mono, Ico,
  fmt, fmtMoney, useMobileMenu,
} from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { PURCHASE_TABS } from "../../gestion-achat/PurchaseTabsLayout";
import { getSalesTabsForUser } from "../../gestion-vente/SalesTabsLayout";
import { PAYMENT_CONFIG, permsFor } from "./paymentsMobileConfig";
import {
  getSuggestedTreasuryAccountId,
  useCommercialPaymentRoutes,
} from "../../hooks/useCommercialTreasurySuggestion";
import {
  deletePayment, fetchEntities, fetchPaymentDetail, fetchPayments, savePayment,
} from "./paymentsMobileApi";

const PAGE = 20;
const idOf = (v) => (v && typeof v === "object" ? v.id : v);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const parseDecimal = (v) => num(String(v ?? "").replace(/\s/g, "").replace(",", "."));
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("fr-FR");
};

export default function PaymentsMobile({ paymentType = "out", onMenu: onMenuProp }) {
  const cfg = PAYMENT_CONFIG[paymentType];
  const perms = permsFor(cfg);

  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();

  const companyId = idOf(selectedCompany);
  const warehouseId = idOf(selectedWarehouse);

  const can = {
    create: hasPermission(perms.create),
    edit: hasPermission(perms.edit),
    delete: hasPermission(perms.delete),
  };

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const debounceRef = useRef(null);

  const [entities, setEntities] = useState([]);

  const [navOpen, setNavOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  /* ---------- formulaire ---------- */
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [allocations, setAllocations] = useState({});   // {order_id: montant}
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  /* ---------- formulaire ---------- */
  const balances = usePaymentOrderOptions({ userId: user?.id, companyId, warehouseId,
    paymentType: cfg.paymentType, entityId: form?.entity_id,
    editingPaymentId: editing?.id, active: formOpen,
    enabled: editing ? can.edit : can.create });
  const formChoices = usePaymentFormChoices({ userId: user?.id, companyId, warehouseId,
    paymentType: cfg.paymentType, active: formOpen,
    enabled: editing ? can.edit : can.create });
  const accounts = formChoices.treasuryAccounts;
  const modes = formChoices.paymentModes;
  const latestAllocations = useRef(allocations);
  latestAllocations.current = allocations;
  useEffect(() => {
    if (formOpen && balances.data) setUnpaidOrders((previous) => mergePaymentOrders(
      balances.data.orders, previous.map((row) => ({ ...row,
        selected: latestAllocations.current[row.id] !== undefined }))));
  }, [formOpen, balances.data]);
  const previousContext = useRef(`${user?.id}:${companyId}:${warehouseId}:${paymentType}`);
  useEffect(() => {
    const context = `${user?.id}:${companyId}:${warehouseId}:${paymentType}`;
    if (previousContext.current !== context) {
      previousContext.current = context;
      setFormOpen(false); setEditing(null); setForm(null);
      setUnpaidOrders([]); setAllocations({});
    }
  }, [user?.id, companyId, warehouseId, paymentType]);

  const { routes: paymentRoutes, loaded: routesLoaded } =
    useCommercialPaymentRoutes(warehouseId, formOpen);

  useEffect(() => {
    if (!formOpen || !routesLoaded || !form?.payment_mode_id || form.treasury_account_id) return;
    const accountId = getSuggestedTreasuryAccountId(form.payment_mode_id, paymentRoutes, accounts);
    if (accountId) {
      setForm((current) => current && !current.treasury_account_id
        ? { ...current, treasury_account_id: String(accountId) }
        : current);
    }
  }, [formOpen, routesLoaded, form?.payment_mode_id, form?.treasury_account_id, paymentRoutes, accounts]);

  const otherTabs = useMemo(() => {
    const tabs =
      paymentType === "out"
        ? PURCHASE_TABS
        : getSalesTabsForUser(user, selectedCompany);
    return tabs.filter((t) => hasPermission(t.permission) && t.path !== cfg.backPath);
  }, [paymentType, user, selectedCompany, hasPermission, cfg.backPath]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAppliedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const load = useCallback(
    async (p) => {
      if (!warehouseId) { setRows([]); setTotal(0); return; }
      setLoading(true);
      try {
        const res = await fetchPayments(cfg, {
          page: p, limit: PAGE, warehouseId,
          search: appliedSearch, entityId: entityFilter || null,
          dateFrom, dateTo,
        });
        setRows((prev) => (p === 1 ? res.rows : [...prev, ...res.rows]));
        setTotal(res.total);
      } catch {
        message.error("Erreur lors du chargement des paiements");
      } finally {
        setLoading(false);
      }
    },
    [cfg, warehouseId, appliedSearch, entityFilter, dateFrom, dateTo]
  );

  useEffect(() => { setPage(1); load(1); }, [load]);

  useEffect(() => {
    if (!warehouseId) { setEntities([]); return; }
    let alive = true;
    fetchEntities(cfg, { warehouseId, companyId })
      .then((e) => alive && setEntities(e))
      .catch(() => alive && setEntities([]));
    return () => { alive = false; };
  }, [cfg, warehouseId, companyId]);



  const loadMore = () => { const p = page + 1; setPage(p); load(p); };
  const reload = () => { setPage(1); load(1); };

  /* ---------- détail ---------- */
  const openDetail = async (payment) => {
    try {
      setDetail(await fetchPaymentDetail(payment.id));
    } catch {
      message.error("Erreur lors du chargement des détails du paiement");
    }
  };

  // Deep-link ?payment_id=<id>, comme le desktop.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("payment_id");
    if (id) openDetail({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const askDelete = (payment) => {
    if (payment.online_order_id) {
      message.info("Ce paiement est piloté depuis la commande e-commerce associée.");
      return;
    }
    setDetail(null);
    Modal.confirm({
      title: "Supprimer ce paiement ?",
      content: "Les montants payés et les statuts des commandes associées seront mis à jour.",
      okText: "Oui", okType: "danger", cancelText: "Non", centered: true,
      onOk: async () => {
        try { await deletePayment(payment.id); void balances.invalidate(); message.success("Paiement supprimé"); reload(); }
        catch { message.error("Erreur lors de la suppression"); }
      },
    });
  };

  const openCreate = () => {
    if (!warehouseId) return message.warning("Veuillez sélectionner un établissement");
    setEditing(null);
    setUnpaidOrders([]); setAllocations({});
    setForm({
      entity_id: "", payment_date: todayIso(), payment_mode_id: "",
      treasury_account_id: "",
      amount: "", notes: "",
    });
    setFormOpen(true);
  };

  const openEdit = (payment) => {
    if (payment.online_order_id) {
      message.info("Ce paiement est piloté depuis la commande e-commerce associée.");
      return;
    }
    setDetail(null);
    setEditing(payment);
    setForm({
      entity_id: String(payment.user_id ?? ""),
      payment_date: String(payment.date || todayIso()).slice(0, 10),
      payment_mode_id: String(payment.payment_mode_id ?? ""),
      treasury_account_id: payment.treasury_account_id ? String(payment.treasury_account_id) : "",
      amount: String(num(payment.amount)),
      notes: payment.notes || "",
    });
    const attached = (payment.orders || []).map((o) => ({
      id: Number(o.order_id ?? o.id), invoice_number: o.invoice_number,
      order_date: o.order_date ?? o.date,
      due_amount: num(o.order_due_amount), total: num(o.order_total),
    }));
    setUnpaidOrders(attached);

    const existing = {};
    attached.forEach((o, i) => {
      existing[o.id] = num(payment.orders[i].amount_applied ?? payment.orders[i].amount);
    });
    setAllocations(existing);
    setFormOpen(true);
  };

  const closeForm = () => { if (!saving) { setFormOpen(false); setEditing(null); setAllocations({}); } };

  const dueOf = (o) => num(o.due_amount ?? o.order_total ?? o.total);

  const toggleOrder = (o) => {
    setAllocations((prev) => {
      const next = { ...prev };
      if (next[o.id] !== undefined) delete next[o.id];
      else next[o.id] = dueOf(o);
      return next;
    });
  };

  const setAllocation = (o, raw) => {
    const capped = Math.max(0, parseDecimal(raw));
    setAllocations((prev) => ({ ...prev, [o.id]: capped }));
  };

  const selectedOrders = useMemo(
    () => Object.entries(allocations).map(([order_id, amount]) => ({ order_id: Number(order_id), amount: num(amount) })),
    [allocations]
  );
  const allocatedTotal = useMemo(
    () => selectedOrders.reduce((s, o) => s + o.amount, 0),
    [selectedOrders]
  );

  const amountNum = form ? parseDecimal(form.amount) : 0;
  const formValid = Boolean(
    form?.entity_id && form?.payment_date && form?.payment_mode_id && amountNum > 0 &&
    (!cfg.withTreasury || form?.treasury_account_id)
  );

  const doSave = async () => {
    if (submitting.current) return;     // le desktop n'a aucune protection : un double-tap suffirait
    submitting.current = true;
    setSaving(true);

    const payload = {
      company_id: companyId,
      warehouse_id: warehouseId,
      payment_type: cfg.paymentType,
      date: form.payment_date,
      amount: amountNum,
      payment_mode_id: Number(form.payment_mode_id),
      user_id: Number(form.entity_id),
      notes: form.notes,
      orders: selectedOrders,
      ...(cfg.withTreasury ? { treasury_account_id: Number(form.treasury_account_id) } : {}),
      ...(cfg.withStaffUser ? { staff_user_id: user?.id } : {}),
    };

    try {
      await balances.verify(selectedOrders);
      await savePayment(editing?.id, payload);
      void balances.invalidate();
      message.success(editing ? "Paiement mis à jour avec succès" : "Paiement créé avec succès");
      setFormOpen(false); setEditing(null); setAllocations({});
      reload();
    } catch (err) {
      if (err.response?.data?.code === "ORDER_PAYMENT_BALANCE_CHANGED") {
        void balances.refresh().catch(() => {});
      }
      message.error(`Erreur lors de l'enregistrement: ${err?.response?.data?.error || err.message}`);
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  // Garde-fous du desktop (PaymentSortant.tsx:887 et :902).
  const submit = () => {
    if (!formValid) return message.warning("Renseignez l'entité, la date, le mode de paiement, le compte de trésorerie et le montant");

    if (!selectedOrders.length) {
      return Modal.confirm({
        title: "Aucune commande sélectionnée",
        content: "Ce paiement ne sera rattaché à aucune commande. Continuer ?",
        okText: "Continuer", cancelText: "Annuler", centered: true, onOk: doSave,
      });
    }
    if (Math.abs(amountNum - allocatedTotal) > 1) {
      return Modal.confirm({
        title: "Montants différents",
        content: `Le montant saisi (${fmtMoney(amountNum)}) diffère du total alloué (${fmtMoney(allocatedTotal)}). Continuer ?`,
        okText: "Continuer", cancelText: "Annuler", centered: true, onOk: doSave,
      });
    }
    doSave();
  };

  /* ---------- rendu ---------- */
  if (!warehouseId) {
    return (
      <ProductScreen>
        <Appbar title={cfg.title} onMenu={onMenu} />
        <div className="body">
          <EmptyState icon="warehouse" title="Aucun établissement sélectionné" text={`Choisissez un établissement pour voir les ${cfg.title.toLowerCase()}.`} />
        </div>
      </ProductScreen>
    );
  }

  const activeFilters = (entityFilter ? 1 : 0) + (dateFrom || dateTo ? 1 : 0);
  const entityOptions = entities.map((e) => ({ value: String(e.id), label: e.name }));
  const formEntityOptions = formChoices.entities.map((e) => ({
    value: String(e.user_id || e.id), label: e.name || e.company_name || "Sans nom",
  }));

  return (
    <ProductScreen>
      <Appbar
        title={cfg.title} onMenu={onMenu}
        subtitle={`${fmt(total)} ${total > 1 ? "paiements" : "paiement"}`}
        action={
          otherTabs.length ? (
            <button className="iconbtn" aria-label="Aller à un autre onglet" onClick={() => setNavOpen(true)}>
              <Ico name="list-ordered" />
            </button>
          ) : null
        }
      />

      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un n° de transaction…" />
          </div>
          <button
            className="iconbtn" aria-label="Filtres" aria-pressed={activeFilters > 0}
            style={activeFilters ? { background: "var(--blue)", color: "#fff" } : undefined}
            onClick={() => setFiltersOpen(true)}
          >
            <Ico name="sliders-horizontal" />
          </button>
        </div>
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement des paiements" text="Synchronisation en cours." />
        ) : !rows.length ? (
          <EmptyState
            icon="banknote"
            title={appliedSearch || activeFilters ? "Aucun résultat" : "Aucun paiement"}
            text={appliedSearch || activeFilters
              ? "Aucun paiement ne correspond à vos filtres."
              : `Les ${cfg.title.toLowerCase()} enregistrés apparaîtront ici.`}
          />
        ) : (
          <div className="list">
            {rows.map((p) => (
              <button key={p.id} className="rcard" onClick={() => openDetail(p)}>
                <Mono name={p.payment_number || "P"} />
                <div className="rcard__main">
                  <div className="rcard__name">{p.payment_number}</div>
                  <div className="rcard__meta">
                    {p.payment_mode_name ? <Pill tone="info" label={p.payment_mode_name} /> : null}
                    {p.online_order_id ? <Pill tone="ok" label={`E-commerce · ${p.ecommerce_order_number}`} /> : null}
                  </div>
                  <div className="rcard__meta">
                    <span className="rcard__sub">{fmtDate(p.date)} · {p.entity_name || cfg.entityLabel}</span>
                  </div>
                </div>
                <span className="rcard__price">{fmtMoney(p.amount)}</span>
              </button>
            ))}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      {can.create ? <Fab onClick={openCreate} label="Nouveau paiement" /> : null}

      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…">
        {otherTabs.map((t) => (
          <ActionRow key={t.key} icon="chevron-right" label={t.label}
            onClick={() => { setNavOpen(false); navigate(t.path); }} />
        ))}
      </Sheet>

      {/* feuille : filtres */}
      <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filtres">
        <Field label={cfg.entityLabel}>
          <SelectInput value={entityFilter} onChange={(v) => setEntityFilter(v || "")}
            options={entityOptions} placeholder={`Tous les ${cfg.entityLabel.toLowerCase()}s`} />
        </Field>
        <Field label="Période">
          <div style={{ display: "flex", gap: 10 }}>
            <div className="ctrl" style={{ flex: 1 }}>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="ctrl" style={{ flex: 1 }}>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
        </Field>
        <div className="sheet__actions">
          <button className="btn btn--ghost" onClick={() => { setEntityFilter(""); setDateFrom(""); setDateTo(""); }}>
            Réinitialiser
          </button>
          <button className="btn btn--primary" onClick={() => setFiltersOpen(false)}>
            <Ico name="check" />Appliquer
          </button>
        </div>
      </Sheet>

      {/* feuille : détail */}
      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)}
        title={detail?.payment_number} subtitle={detail?.entity_name}>
        {detail ? (
          <>
            <DetailRow label="Date" value={fmtDate(detail.date)} />
            <DetailRow label="Montant" value={fmtMoney(detail.amount)} />
            <DetailRow label="Mode de paiement" value={detail.payment_mode_name || "—"} />
            <DetailRow label="Établissement" value={detail.warehouse_name || "—"} />
            <DetailRow label="Remarques" value={detail.notes || "Aucune"} />
            {detail.online_order_id ? <Note>Paiement piloté par la commande e-commerce {detail.ecommerce_order_number}. Enregistrez son remboursement depuis Commandes en ligne.</Note> : null}

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Commandes associées</div>
            {!detail.orders?.length && !detail.ecommerce_orders?.length ? (
              <Note>Ce paiement n'est rattaché à aucune commande.</Note>
            ) : (
              detail.orders.map((o, i) => (
                <DetailRow key={o.order_id ?? i} label={o.invoice_number || `Commande ${o.order_id}`}
                  value={`${fmtMoney(o.amount_applied)} / ${fmtMoney(o.order_total)}`} />
              ))
            )}
            {(detail.ecommerce_orders || []).map((o, i) => (
              <DetailRow key={o.online_order_id ?? i} label={o.order_number || `Commande e-commerce ${o.online_order_id}`}
                value={`${o.event_type === "refund" ? "Remboursement" : "Encaissement"} · ${fmtMoney(o.amount_applied)}`} />
            ))}

            <div style={{ marginTop: 16 }}>
              {can.edit && !detail.online_order_id ? <ActionRow icon="pencil" label="Modifier le paiement" onClick={() => openEdit(detail)} /> : null}
              {can.delete && !detail.online_order_id ? <ActionRow icon="trash-2" label="Supprimer le paiement" danger onClick={() => askDelete(detail)} /> : null}
            </div>
          </>
        ) : null}
      </Sheet>

      {/* feuille : formulaire */}
      <Sheet open={formOpen} onClose={closeForm}
        title={editing ? "Modifier le paiement" : "Nouveau paiement"}
        subtitle={`Réglez une ou plusieurs commandes de ce ${cfg.entityLabel.toLowerCase()}.`}>
        {form ? (
          <>
            <BusinessResourceNotice resource={formChoices} label="listes de choix" />
            <BusinessResourceNotice resource={balances} label="commandes et soldes" />
            {unpaidOrders.some((row) => allocations[row.id] !== undefined && Number(allocations[row.id]) > dueOf(row) + 0.0001) &&
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message="Le solde de certaines commandes a changé."
                description="Les affectations sont conservées. Vérifiez les montants avant d'enregistrer." />}

            {form.entity_id && <Button loading={balances.loading} style={{ marginBottom: 12 }}
              onClick={() => { void balances.refresh().catch(() => {}); }}>Actualiser les commandes</Button>}
            <Field label={`${cfg.entityLabel} *`}>
              {editing ? (
                <TextInput readOnly value={entities.find((e) => String(e.id) === form.entity_id)?.name || detail?.entity_name || ""} />
              ) : (
                <SelectInput
                  value={form.entity_id}
                  onChange={(v) => { setForm((f) => ({ ...f, entity_id: v || "" })); setAllocations({}); setUnpaidOrders([]); }}
                  options={formEntityOptions}
                  placeholder={`Sélectionner un ${cfg.entityLabel.toLowerCase()}`}
                />
              )}
            </Field>

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>
              Commandes à régler {unpaidOrders.length ? <small>({unpaidOrders.length})</small> : null}
            </div>
            {!form.entity_id ? (
              <Note>Sélectionnez d'abord un {cfg.entityLabel.toLowerCase()}.</Note>
            ) : !unpaidOrders.length ? (
              <Note>{balances.status === "verified" ? `Aucune commande impayée pour ce ${cfg.entityLabel.toLowerCase()}.` : "Lecture des commandes à régler…"}</Note>
            ) : (
              <>
                {unpaidOrders.map((o) => {
                  const selected = allocations[o.id] !== undefined;
                  return (
                    <div key={o.id} className="preview" style={{ display: "block", marginBottom: 10 }}>
                      <button
                        onClick={() => toggleOrder(o)}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: "none", border: 0, padding: 0 }}
                      >
                        <Ico name={selected ? "check-circle-2" : "circle"}
                          style={{ width: 22, height: 22, color: selected ? "var(--blue)" : "var(--muted-2)" }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="preview__name">{o.invoice_number}</div>
                          <div className="preview__slug">
                            {fmtDate(o.order_date || o.date)} · dû {fmtMoney(dueOf(o))}
                          </div>
                        </div>
                      </button>
                      {selected ? (
                        <div style={{ marginTop: 10 }}>
                          <Field label={`Montant à payer (max ${fmtMoney(dueOf(o))})`}>
                            <TextInput value={String(allocations[o.id])} inputMode="decimal"
                              onChange={(v) => setAllocation(o, v)} />
                          </Field>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                <DetailRow label="Total sélectionné" value={fmtMoney(allocatedTotal)} />
              </>
            )}

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Paiement</div>
            <Field label="Date *">
              <div className="ctrl">
                <input type="date" value={form.payment_date}
                  onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))} />
              </div>
            </Field>
            <Field label="Mode de paiement *">
              <SelectInput value={form.payment_mode_id}
                onChange={(v) => {
                  const accountId = routesLoaded
                    ? getSuggestedTreasuryAccountId(v, paymentRoutes, accounts)
                    : undefined;
                  setForm((f) => ({ ...f, payment_mode_id: v || "", treasury_account_id: accountId ? String(accountId) : "" }));
                }}
                options={modes.map((m) => ({ value: String(m.id), label: m.name }))}
                placeholder="Sélectionner un mode" />
            </Field>
            {cfg.withTreasury ? (
              <Field label="Compte de trésorerie *">
                {!accounts.length && formChoices.status === "verified" ? (
                  <Note>Aucun compte de trésorerie disponible pour cet établissement.</Note>
                ) : (
                  <SelectInput value={form.treasury_account_id}
                    onChange={(v) => setForm((f) => ({ ...f, treasury_account_id: v || "" }))}
                    options={accounts.map((a) => ({ value: String(a.id), label: a.name }))}
                    placeholder="Sélectionner un compte" />
                )}
              </Field>
            ) : null}
            <Field label="Montant *">
              <TextInput value={form.amount} inputMode="decimal"
                onChange={(v) => setForm((f) => ({ ...f, amount: v }))} placeholder="0" icon="banknote" />
            </Field>
            <Field label="Remarques" hint="facultatif">
              <TextAreaInput value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} rows={2} />
            </Field>

            <div className="sheet__actions">
              <button className="btn btn--ghost" onClick={closeForm} disabled={saving}>Annuler</button>
              <button className="btn btn--primary" onClick={submit} disabled={saving || !formValid || balances.loading || !balances.data || !formChoices.data}>
                <Ico name={saving ? "loader-circle" : "check"} className={saving ? "spin" : ""} />
                {editing ? "Mettre à jour" : "Enregistrer"}
              </button>
            </div>
          </>
        ) : null}
      </Sheet>
    </ProductScreen>
  );
}
